/** Giao diện chat AI Agent Hub — layout kiểu ChatGPT, skin Toppion. */
import React, { useEffect, useRef, useState, type CSSProperties } from 'react';
import Link from 'next/link';
import { Alert, Avatar, Button, Input, Space, Tag, Tooltip, message, theme } from 'antd';
import { aiAgentApi } from '@/services/knowledge/ai-agent-api';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  DislikeFilled,
  DislikeOutlined,
  FileTextOutlined,
  LikeFilled,
  LikeOutlined,
  LoadingOutlined,
  ReloadOutlined,
  SendOutlined,
  StopOutlined,
} from '@ant-design/icons';
import Image from 'next/image';
import { AiMarkdown } from '@/components/common/aiMarkdown';
import { useAuthStore } from '@/store/useAuthStore';
import type { AiHubBudget } from '@/types/ai-agent';
import { BscApplyOfferCard, type BscApplyOfferState } from './BscApplyOfferCard';
import { ActionPlanPendingCard } from './ActionPlanPendingCard';
import { BscStepPendingCard } from './BscStepPendingCard';
import { agentRunClient } from '@/services/knowledge/agent-run-client';
import { ActionPlanTaskListCard } from './ActionPlanTaskListCard';
import { ActionPlanObjectiveListCard } from './ActionPlanObjectiveListCard';
import { ActionPlanScopePickerCard } from './ActionPlanScopePickerCard';
import { ActionPlanAssigneePickerCard } from './ActionPlanAssigneePickerCard';
import type { ActionPlanAiObjectiveListState } from '@/types/action-plan-v2/ActionPlanAiObjectiveList';
import { isBscStepPlan, type ActionPlanAiPendingPlan, type ActionPlanAiPendingState } from '@/types/action-plan-v2/ActionPlanAiPlan';
import type {
  ActionPlanAiTaskListState,
  ActionPlanAiTaskPickAction,
} from '@/types/action-plan-v2/ActionPlanAiTaskList';
import { ChatLlmConfigPill } from './ChatLlmConfigPill';
import { ChatRunMetaTags } from './ChatRunMetaTags';

/** Bubble Action Plan: bước xử lý hiện nhãn dễ hiểu (như Copilot / ClickUp Brain), không lộ tên tool, số ký tự, model. */
const AP_TOOL_LABELS: Record<string, string> = {
  get_due_tasks: 'Xem việc đến hạn',
  list_my_tasks: 'Xem việc gốc bạn phụ trách',
  list_cong_viec: 'Tìm công việc',
  list_scope: 'Xem phòng ban / nhân viên',
  get_status_summary: 'Thống kê việc theo trạng thái',
  list_objectives: 'Xem mục tiêu',
  search_knowledge: 'Tra kho tri thức',
  prepare_create_action_plan_task: 'Chuẩn bị tạo việc',
  prepare_child_tasks_breakdown: 'Chuẩn bị chia việc con',
  prepare_update_action_plan_task: 'Chuẩn bị sửa việc',
  prepare_delete_action_plan_task: 'Chuẩn bị xoá việc',
  request_assign_to_other: 'Kiểm tra quyền giao việc',
};
const friendlyStepLabel = (label: string) => {
  const m = label.match(/^Tool: (\w+)$/);
  return m ? AP_TOOL_LABELS[m[1]] ?? 'Đang xử lý' : label;
};
import { sanitizeAiAnswerText } from './sanitizeAiAnswer';
import type { ChatLlmCatalog, ChatLlmConfig, ChatTurn } from './useAiChat';
import styles from './AiChatView.module.css';

export interface AiChatViewProps {
  turns: ChatTurn[];
  streaming: boolean;
  lastPrompt: string;
  onSend: (message: string) => void;
  onStop: () => void;
  onApplyOfferChange?: (turnId: string, offer: BscApplyOfferState) => void;
  onActionPlanPendingChange?: (turnId: string, pending: ActionPlanAiPendingState) => void;
  onActionPlanConfirm?: (turnId: string, plan?: ActionPlanAiPendingPlan) => void | Promise<void>;
  /** Việc con hàng loạt: xác nhận tất cả thẻ còn lại. */
  onActionPlanConfirmAll?: (turnId: string) => void | Promise<void>;
  onActionPlanTaskListChange?: (turnId: string, state: ActionPlanAiTaskListState) => void;
  /** Chọn người phụ trách trên thẻ → khoá thẻ + gửi lựa chọn vào chat. */
  onActionPlanAssigneeChange?: (
    turnId: string,
    state: import('@/types/action-plan-v2/ActionPlanAiAssigneeList').ActionPlanAiAssigneeListState,
  ) => void;
  onActionPlanTaskPick?: (turnId: string, action: ActionPlanAiTaskPickAction, taskId: string) => void | Promise<void>;
  onActionPlanObjectivePick?: (turnId: string, objectiveId: string) => void | Promise<void>;
  /** b1–b5 — lọc section trên card Thêm vào trang */
  pageStep?: string;
  llmConfig?: ChatLlmConfig;
  onLlmConfigChange?: (next: ChatLlmConfig) => void;
  llmCatalog?: ChatLlmCatalog | null;
  /** Skill chips dưới ô nhập (vd. AI gợi ý bước) */
  skills?: { key: string; label: string; title?: string; onClick: () => void; disabled?: boolean }[];
  contextLabel?: string;
  suggestions?: string[];
  budget?: AiHubBudget | null;
  headerExtra?: React.ReactNode;
  /** Ngữ cảnh + mẹo đã đưa lên header drawer (tối ưu diện tích) → không vẽ hàng ngữ cảnh. */
  contextInHeader?: boolean;
  assistantName?: string;
}

const StatusIcon = ({ status }: { status: 'running' | 'done' | 'error' }) => {
  if (status === 'running') return <LoadingOutlined aria-label="Đang chạy" />;
  if (status === 'error') return <CloseCircleFilled style={{ color: 'var(--ai-error)' }} aria-label="Lỗi" />;
  return <CheckCircleFilled style={{ color: 'var(--ai-success)' }} aria-label="Xong" />;
};

const getInitials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(-2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || 'B';

export const AiChatView: React.FC<AiChatViewProps> = ({
  turns,
  streaming,
  lastPrompt,
  onSend,
  onStop,
  onApplyOfferChange,
  onActionPlanPendingChange,
  onActionPlanConfirm,
  onActionPlanConfirmAll,
  onActionPlanTaskListChange,
  onActionPlanAssigneeChange,
  onActionPlanTaskPick,
  onActionPlanObjectivePick,
  pageStep,
  llmConfig,
  onLlmConfigChange,
  llmCatalog,
  skills = [],
  contextLabel,
  suggestions = [],
  budget,
  headerExtra,
  contextInHeader = false,
  assistantName = 'Trợ lý BSC',
}) => {
  const { token } = theme.useToken();
  const profile = useAuthStore((s) => s.profile);
  // Bubble Action Plan V2 (có thẻ xác nhận ghi việc) → giao diện thân thiện, ẩn thông số kỹ thuật
  const actionPlanMode = Boolean(onActionPlanConfirm);
  const userName = profile?.tenNhanVien?.trim() || profile?.id?.trim() || 'Bạn';
  const userAvatar = profile?.avatar?.trim() || undefined;
  const [draft, setDraft] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const turnCount = turns.length;

  useEffect(() => {
    const box = scrollRef.current;
    if (!box || !turnCount) return;
    const lastUser = box.querySelectorAll('[data-role="user"]');
    lastUser[lastUser.length - 1]?.scrollIntoView({ block: 'start' });
  }, [turnCount]);

  const submit = () => {
    if (!draft.trim() || streaming) return;
    onSend(draft);
    setDraft('');
  };

  const lastTurn = turns[turns.length - 1];
  const canRetry = !streaming && lastTurn?.role === 'assistant' && lastTurn.status === 'failed' && lastPrompt;
  const budgetLow = budget?.daily_token_budget ? (budget.remaining ?? 0) < budget.daily_token_budget * 0.1 : false;

  return (
    <div
      className={styles.shell}
      style={{
        ['--ai-surface' as string]: token.colorBgContainer,
        ['--ai-muted' as string]: token.colorFillAlter,
        ['--ai-user' as string]: token.colorPrimaryBg,
        ['--ai-border' as string]: token.colorBorderSecondary,
        ['--ai-text' as string]: token.colorText,
        ['--ai-secondary' as string]: token.colorTextSecondary,
        ['--ai-primary' as string]: token.colorPrimary,
        ['--ai-success' as string]: token.colorSuccess,
        ['--ai-error' as string]: token.colorError,
      } as CSSProperties}
    >
      {!contextInHeader ? (
        <div className={styles.contextBar}>
          <div className={styles.contextChips}>
            <span className={styles.chipPrimary}>{contextLabel ? `Đang ở: ${contextLabel}` : 'Trợ lý chung'}</span>
            {budget?.daily_token_budget ? (
              <Tooltip title="Hạn mức token hôm nay của đơn vị">
                <span className={budgetLow ? styles.chipWarn : styles.chip}>
                  Còn {(budget.remaining ?? 0).toLocaleString('vi-VN')} token
                </span>
              </Tooltip>
            ) : null}
          </div>
          {headerExtra ? <div className={styles.contextExtra}>{headerExtra}</div> : null}
        </div>
      ) : null}

      <div className={styles.conversation} ref={scrollRef} aria-live="polite">
        <div className={styles.thread}>
          {turns.length === 0 ? (
            <div className={styles.empty}>
              <div className={styles.emptyBadge}>
                <Image src="/icon/agent.png" alt="" width={40} height={40} unoptimized />
              </div>
              <h2 className={styles.emptyTitle}>Tôi có thể giúp gì cho kế hoạch của bạn?</h2>
              <p className={styles.emptyHint}>
                Trợ lý điều phối chuyên gia BSC và tài liệu đơn vị. Kiểm tra lại số liệu quan trọng trước khi dùng.
              </p>
              <div className={styles.suggestions}>
                {suggestions.map((s) => (
                  <button key={s} type="button" className={styles.suggestion} onClick={() => onSend(s)}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            turns.map((turn) => (
              <div
                key={turn.id}
                data-role={turn.role}
                className={`${styles.turn} ${turn.role === 'user' ? styles.turnUser : styles.turnAssistant}`}
              >
                {turn.role === 'user' ? (
                  <div className={styles.userBubble}>
                    <div className={styles.userMeta}>
                      <Avatar size={26} src={userAvatar} aria-label={`Ảnh đại diện ${userName}`}>
                        {getInitials(userName)}
                      </Avatar>
                      <span>{userName}</span>
                    </div>
                    <div className={styles.userText}>{turn.content}</div>
                  </div>
                ) : (
                  <div className={styles.assistantBlock}>
                    <div className={styles.assistantMeta}>
                      <Image src="/icon/agent.png" alt="" width={26} height={26} unoptimized />
                      <span>{assistantName}</span>
                    </div>
                    {!actionPlanMode ? <ChatRunMetaTags meta={turn.runMeta} /> : null}
                    {actionPlanMode && turn.timeline.some((t) => t.key.startsWith('tool-')) ? (
                      // Gọn 1 dòng (như Copilot): bước đang chạy hiện ngay, xong thì gom, bấm để xem chi tiết
                      <details className={styles.stepsSummary}>
                        <summary>
                          {turn.timeline.some((t) => t.key.startsWith('tool-') && t.status === 'running')
                            ? `Đang ${friendlyStepLabel(
                                turn.timeline.filter((t) => t.key.startsWith('tool-') && t.status === 'running').slice(-1)[0].label,
                              ).toLowerCase()}…`
                            : `Đã tra cứu: ${[
                                ...new Set(turn.timeline.filter((t) => t.key.startsWith('tool-')).map((t) => friendlyStepLabel(t.label))),
                              ].join(' · ')}`}
                        </summary>
                        <div className={styles.timeline}>
                          {turn.timeline
                            .filter((item) => item.key.startsWith('tool-'))
                            .map((item) => (
                              <div
                                key={item.key}
                                className={`${styles.timelineRow} ${styles[`tl_${item.status}`]}`}
                                data-tool={item.label.replace(/^Tool: /, '')}
                              >
                                <StatusIcon status={item.status} />
                                <span className={styles.timelineLabel}>{friendlyStepLabel(item.label)}</span>
                                {item.detail && item.status === 'error' ? (
                                  <span className={styles.timelineDetail}>{item.detail}</span>
                                ) : null}
                              </div>
                            ))}
                        </div>
                      </details>
                    ) : null}
                    {!actionPlanMode && turn.timeline.length > 0 && (
                      <div className={styles.timeline}>
                        {turn.timeline
                          // Bubble Action Plan: bỏ dòng node chung («Trợ lý BSC»), chỉ giữ các bước tra cứu / chuẩn bị
                          .filter((item) => !actionPlanMode || item.key.startsWith('tool-'))
                          .map((item) => (
                            <div
                              key={item.key}
                              className={`${styles.timelineRow} ${styles[`tl_${item.status}`]}`}
                              data-tool={item.label.replace(/^Tool: /, '')}
                            >
                              <StatusIcon status={item.status} />
                              <span className={styles.timelineLabel}>
                                {actionPlanMode ? friendlyStepLabel(item.label) : item.label}
                              </span>
                              {item.detail && (!actionPlanMode || item.status === 'error') ? (
                                <span className={styles.timelineDetail}>{item.detail}</span>
                              ) : null}
                            </div>
                          ))}
                      </div>
                    )}
                    {turn.content ? (
                      turn.status === 'streaming' ? (
                        <div className={styles.streamText}>
                          {sanitizeAiAnswerText(turn.content, { trim: false })}
                          <span className={styles.caret} aria-hidden />
                        </div>
                      ) : (
                        <div className={styles.markdown}>
                          <AiMarkdown>{sanitizeAiAnswerText(turn.content)}</AiMarkdown>
                        </div>
                      )
                    ) : turn.status === 'streaming' ? (
                      <div className={styles.thinking}>
                        <LoadingOutlined />
                        <span>Đang suy nghĩ…</span>
                      </div>
                    ) : null}
                    {turn.applyOffer && onApplyOfferChange ? (
                      <BscApplyOfferCard
                        offer={turn.applyOffer}
                        pageStep={pageStep}
                        onChange={(next) => onApplyOfferChange(turn.id, next)}
                      />
                    ) : null}
                    {turn.actionPlanAssigneeList && onActionPlanAssigneeChange ? (
                      <ActionPlanAssigneePickerCard
                        state={turn.actionPlanAssigneeList}
                        onPick={(picked) => {
                          onActionPlanAssigneeChange(turn.id, { ...turn.actionPlanAssigneeList!, picked });
                          // Kèm mã người: tên trùng / kiêm nhiệm cùng phòng vẫn chốt đúng người (engine đọc «(mã …)»)
                          onSend(`Giao cho ${picked.ten}${picked.tenBoPhan ? ` — ${picked.tenBoPhan}` : ''} (mã ${picked.id})`);
                        }}
                      />
                    ) : null}
                    {turn.actionPlanScopeList && onActionPlanTaskListChange ? (
                      <ActionPlanScopePickerCard
                        state={turn.actionPlanScopeList}
                        onShowTaskList={(next) => onActionPlanTaskListChange(turn.id, next)}
                      />
                    ) : null}
                    {turn.actionPlanTaskList && onActionPlanTaskListChange && onActionPlanTaskPick ? (
                      <ActionPlanTaskListCard
                        state={turn.actionPlanTaskList}
                        onListChange={(next) => onActionPlanTaskListChange(turn.id, next)}
                        onPick={(action, taskId) => void onActionPlanTaskPick(turn.id, action, taskId)}
                      />
                    ) : null}
                    {turn.actionPlanObjectiveList && onActionPlanObjectivePick ? (
                      <ActionPlanObjectiveListCard
                        state={turn.actionPlanObjectiveList as ActionPlanAiObjectiveListState}
                        onPick={(objectiveId) => {
                          const objList = (turn.actionPlanObjectiveList as ActionPlanAiObjectiveListState).list;
                          // Quản lý / admin: trợ lý phải hỏi người phụ trách → gửi lựa chọn vào chat thay vì tự dựng thẻ
                          if (objList.quyenGhi && objList.quyenGhi !== 'self') {
                            const ten = objList.items.find((i) => i.id === objectiveId)?.ten || objectiveId;
                            onSend(`Chọn mục tiêu «${ten}» (mã ${objectiveId})`);
                            return;
                          }
                          void onActionPlanObjectivePick(turn.id, objectiveId);
                        }}
                      />
                    ) : null}
                    {turn.actionPlanPending && turn.actionPlanPending.status !== 'dismissed' && isBscStepPlan(turn.actionPlanPending.plan) ? (
                      <BscStepPendingCard
                        key={turn.actionPlanPending.interruptId}
                        pending={turn.actionPlanPending}
                        onConfirm={(mergedPlan) => void onActionPlanConfirm?.(turn.id, mergedPlan)}
                        onDismiss={() => {
                          const cur = turn.actionPlanPending!;
                          // Bỏ qua = từ chối điểm chờ duyệt trên engine (lệnh không còn dùng lại được)
                          if (cur.runId && cur.interruptId) {
                            void agentRunClient
                              .resume(cur.runId, { interrupt_id: cur.interruptId, decision: 'reject' })
                              .catch(() => undefined);
                          }
                          onActionPlanPendingChange?.(turn.id, { ...cur, status: 'dismissed' });
                        }}
                      />
                    ) : turn.actionPlanPending && turn.actionPlanPending.status !== 'dismissed' ? (
                      <ActionPlanPendingCard
                        pending={turn.actionPlanPending}
                        onConfirm={(mergedPlan) => void onActionPlanConfirm?.(turn.id, mergedPlan)}
                        onAsk={(text) => onSend(text)}
                        onConfirmAll={
                          onActionPlanConfirmAll && (turn.actionPlanPendingQueue?.length ?? 0) > 0
                            ? () => void onActionPlanConfirmAll(turn.id)
                            : undefined
                        }
                        onDismiss={() =>
                          onActionPlanPendingChange?.(turn.id, {
                            ...turn.actionPlanPending!,
                            status: 'dismissed',
                          })}
                        onPlanChange={(mergedPlan) =>
                          onActionPlanPendingChange?.(turn.id, {
                            ...turn.actionPlanPending!,
                            plan: mergedPlan,
                          })}
                      />
                    ) : null}
                    {turn.citations.length > 0 && (
                      <div className={styles.citations}>
                        {turn.citations.map((c, i) => (
                          <Tooltip key={`${c.doc_id}-${i}`} title={c.snippet}>
                            {c.url?.startsWith('/') ? (
                              <Link href={c.url} target="_blank">
                                <Tag icon={<FileTextOutlined />} bordered={false} className={styles.citeTag} color="processing">
                                  {c.n ? `[${c.n}] ` : ''}{c.title || c.filename}
                                </Tag>
                              </Link>
                            ) : (
                              <Tag icon={<FileTextOutlined />} bordered={false} className={styles.citeTag}>
                                {c.n ? `[${c.n}] ` : ''}{c.title || c.filename}
                              </Tag>
                            )}
                          </Tooltip>
                        ))}
                      </div>
                    )}
                    {turn.status === 'completed' && turn.messageId ? <FeedbackBar turn={turn} /> : null}
                    {turn.status === 'cancelled' && <div className={styles.statusNote}>Đã dừng.</div>}
                    {turn.status === 'failed' && (
                      <Alert type="error" showIcon message={turn.error || 'Có lỗi khi tạo câu trả lời.'} className={styles.errorAlert} />
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      <div className={styles.composerShell}>
        <div className={styles.composerInner}>
          <div className={styles.composerCard}>
            <label className={styles.srOnly} htmlFor="ai-hub-chat-message">Tin nhắn</label>
            <Input.TextArea
              id="ai-hub-chat-message"
              className={styles.composerInput}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onPressEnter={(e) => {
                if (e.shiftKey || e.nativeEvent.isComposing) return;
                e.preventDefault();
                submit();
              }}
              placeholder="Hỏi về chiến lược, KPI, tài liệu…"
              autoSize={{ minRows: 2, maxRows: 6 }}
              maxLength={8000}
              aria-label="Tin nhắn"
              disabled={streaming}
              variant="borderless"
            />
            <div className={styles.composerBar}>
              <div className={styles.composerBarLeft}>
                {llmConfig && onLlmConfigChange ? (
                  <ChatLlmConfigPill
                    value={llmConfig}
                    onChange={onLlmConfigChange}
                    providers={llmCatalog?.providers ?? []}
                    catalogLoading={llmCatalog == null}
                    tenantHasApiKey={llmCatalog?.tenantHasApiKey}
                    tenantApiKeyMasked={llmCatalog?.tenantApiKeyMasked}
                    disabled={streaming}
                  />
                ) : null}
                {skills.map((skill) => (
                  <Tooltip key={skill.key} title={skill.title || skill.label}>
                    <button
                      type="button"
                      className={styles.skillChip}
                      onClick={skill.onClick}
                      disabled={streaming || skill.disabled}
                      aria-label={skill.label}
                    >
                      {skill.label}
                    </button>
                  </Tooltip>
                ))}
                {canRetry ? (
                  <Button size="small" type="text" icon={<ReloadOutlined />} onClick={() => onSend(lastPrompt)}>
                    Thử lại
                  </Button>
                ) : null}
              </div>
              <div className={styles.composerBarRight}>
                <span className={styles.composerHint}>Enter gửi · Shift+Enter xuống dòng</span>
                {streaming ? (
                  <button type="button" className={styles.sendBtnStop} onClick={onStop} aria-label="Dừng">
                    <StopOutlined />
                  </button>
                ) : (
                  <button
                    type="button"
                    className={styles.sendBtn}
                    onClick={submit}
                    disabled={!draft.trim()}
                    aria-label="Gửi"
                  >
                    <SendOutlined />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiChatView;

/** 👍/👎 cho câu trả lời AI (engine lưu phiếu + đưa câu 👎 vào danh sách Giám sát › Phản hồi / bộ eval). */
const FeedbackBar: React.FC<{ turn: ChatTurn }> = ({ turn }) => {
  const [rating, setRating] = useState<number | null>(turn.feedback ?? null);
  const [comment, setComment] = useState('');
  const [askComment, setAskComment] = useState(false);
  const [busy, setBusy] = useState(false);

  const vote = async (value: 1 | -1, note = '') => {
    if (!turn.messageId) return;
    const next = rating === value && !note ? 0 : value;
    setBusy(true);
    const res = await aiAgentApi.messageFeedback(turn.messageId, next, note, typeof window !== 'undefined' ? window.location.pathname : undefined)
      .catch(() => null);
    setBusy(false);
    if (!res?.success) return void message.error(res?.message || 'Không gửi được phản hồi');
    setRating(next || null);
    setAskComment(next === -1 && !note);
    if (note) {
      setComment('');
      message.success('Cảm ơn góp ý của bạn');
    }
  };

  return (
    <div style={{ marginTop: 6 }}>
      <Space size={2}>
        <Tooltip title="Câu trả lời hữu ích">
          <Button type="text" size="small" disabled={busy} aria-label="Hữu ích" icon={rating === 1 ? <LikeFilled /> : <LikeOutlined />} onClick={() => void vote(1)} />
        </Tooltip>
        <Tooltip title="Chưa tốt">
          <Button type="text" size="small" disabled={busy} aria-label="Chưa tốt" icon={rating === -1 ? <DislikeFilled /> : <DislikeOutlined />} onClick={() => void vote(-1)} />
        </Tooltip>
      </Space>
      {askComment ? (
        <Space.Compact style={{ width: '100%', marginTop: 4 }}>
          <Input size="small" value={comment} onChange={(e) => setComment(e.target.value)} maxLength={2000} placeholder="Chưa tốt ở đâu? (tuỳ chọn)" onPressEnter={() => comment.trim() && void vote(-1, comment.trim())} />
          <Button size="small" disabled={!comment.trim() || busy} onClick={() => void vote(-1, comment.trim())}>Gửi</Button>
        </Space.Compact>
      ) : null}
    </div>
  );
};
